import dayjs from 'dayjs';
import { FaceSheetFormType } from '../../pages/faceSheet/FaceSheetScreen';
import {
  AddReReferralFaceSheet,
  Client,
  ClientsListType,
  DashboardOfflineData,
  FaceSheetType,
  OfflineData,
  ServiceCoordinatorsType,
  SyncOfflineData,
} from '../../utils/type';
import { getCurrentWeekSundayAsDate } from '../../utils/utilities';
import { ApiMessage, ApiResponseHandler } from '../ApiResponseHandler';
import ThomApiClient from '../ThomApiClient';
import { REFERRAL_PROGRAM_KEY } from '../Constant';

export type DashboardDetailsResponse = {
  totalClientsCount: number;
  clients: ClientsListType[];
};

export type ReferralResponseType = {
  referralId: string;
  dphId: string | null;
  programId: string;
  programAcronym: string;
  programName: string;
  isSecondaryProgramServices: boolean;
};

export const getClientsDetails = async (
  page: number,
  size: number,
  filterData: string,
  sort: string,
  sortType: string,
  searchclient: string,
): Promise<DashboardDetailsResponse> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<DashboardDetailsResponse>(
      `clients?page=${page}&size=${size}&filter=${filterData}&sort=${sort}&sortType=${sortType}&search=${searchclient}&appSource=TCS`,
    ),
  );
};

export const getAllClients = async (
  active: boolean,
  search: string,
  clientId?: string[] | null,
): Promise<Client[]> => {
  const clientIds = clientId && clientId.length > 0 ? clientId : [];
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `clients/all?active=${active}&search=${search}`,
      clientIds,
    ),
  );
};

export const getFacesheetDetails = async (
  clientId: string,
): Promise<FaceSheetType> => {
  const timezone = encodeURIComponent(
    dayjs(getCurrentWeekSundayAsDate()).format('Z'),
  );
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<FaceSheetType>(
      `client/faceSheet?clientId=${clientId}&timezone=${timezone}&referralProgramId=${
        referralProgramId ?? ''
      }`,
    ),
  );
};

export const getAllReferrals = async (
  clientId: string,
  defaultReferralId: string,
): Promise<ReferralResponseType[]> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ReferralResponseType[]>(
      `client/referral_info?defaultReferralId=${defaultReferralId}&clientId=${clientId}`,
    ),
  );
};

export const getListByRole = async (
  type: string,
  employeeIds?: string[],
): Promise<ServiceCoordinatorsType[]> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ServiceCoordinatorsType[]>(
      `employee/listByRole?employeeRole=${type}&empIds=${employeeIds || []}`,
    ),
  );
};

export const addReReferral = async (
  param: AddReReferralFaceSheet,
  clientId: string,
): Promise<{ referralId: string }> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `client/re-referral?clientId=${clientId}`,
      param,
    ),
  );
};

export const updateFacesheetDetails = async (
  param: Partial<FaceSheetFormType>,
  clientId: string,
): Promise<ApiMessage> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put(
      `client/faceSheet?clientId=${clientId}&referralProgramId=${
        referralProgramId ?? ''
      }`,
      param,
    ),
  );
};

export const postServiceCoordinators = async (
  serviceCoordinatorId: object,
  clientId: string,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put(
      `client/serviceCoordinator?clientId=${clientId}`,
      serviceCoordinatorId,
    ),
  );
};

export const getOfflineData = async (): Promise<DashboardOfflineData> => {
  const timezone = encodeURIComponent(
    dayjs(getCurrentWeekSundayAsDate()).format('Z'),
  );
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<DashboardOfflineData>(
      `offline?timezone=${timezone}`,
    ),
  );
};

export const syncOfflineData = async (
  offlineData: OfflineData,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(
    dayjs(getCurrentWeekSundayAsDate()).format('Z'),
  );
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `offline?timezone=${timezone}`,
      offlineData,
    ),
  );
};

export const syncEmployeeOfflineData = async (
  offlineData: SyncOfflineData,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `offline?isLastSync=` + offlineData.isLastSync,
      offlineData,
    ),
  );
};

export const deleteSession = async (): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().delete<ApiMessage>(`/offline/session`),
  );
};
